/**
 * Typed calls into the Rust side of dictation anywhere. Rust owns the last
 * three dictations and everything after the transcript (AI, clipboard,
 * paste) — the text never lives in a webview store, and reports carry codes
 * only.
 */
import { invoke } from "@tauri-apps/api/core";
import { isTauri } from "@/lib/is-tauri";

export type DictationAnywhereAiState =
  | "off"
  | "pending"
  | "ok"
  | "not_improved"
  | "interrupted";

export type DictationAnywhereDelivery =
  | "pending"
  | "pasted"
  | "copied"
  | "failed";

export type DictationAnywhereScreenRecording = {
  clip_id: string;
  attention_session_id: string;
  original_path: string;
  started_ms: number;
  duration_ms: number;
};

export type DictationAnywhereRecord = {
  id: string;
  created_at_ms: number;
  raw: string;
  /** The prompt applied (`["Default"]` or a numbered prompt's name); empty
   *  when the text was delivered as spoken. */
  prompt_names: string[];
  enhanced: string | null;
  ai: DictationAnywhereAiState;
  /** A fixed diagnostic code, never text. */
  ai_code: string | null;
  delivery: DictationAnywhereDelivery;
  delivery_code: string | null;
  /** Marked screenshots saved with the dictation. */
  image_count: number;
  screen_recordings?: DictationAnywhereScreenRecording[];
  /** Fallback destination when a clip has not finished saving. */
  screen_recording_folder?: string | null;
};

/** The last three dictations, newest first. */
export async function getDictationAnywhereHistory(): Promise<
  DictationAnywhereRecord[]
> {
  if (!isTauri()) return [];
  return invoke<DictationAnywhereRecord[]>("dictation_anywhere_history");
}

export async function clearDictationAnywhereHistory(): Promise<void> {
  if (!isTauri()) return;
  await invoke("dictation_anywhere_clear_history");
}

/** One AI prompt: its name for the history, its instruction for the model. */
export type DictationAnywherePrompt = { name: string; instruction: string };

export type DictationAnywhereDeliverRequest = {
  recording_id: string;
  text: string;
  /** The one prompt the stop applies; empty to deliver as spoken. */
  prompts: DictationAnywherePrompt[];
  auto_paste: boolean;
  connected_runtimes: string[];
  /** The transcript arrived after the flow gave up on it: copy, never paste. */
  late: boolean;
  screen_recordings?: DictationAnywhereScreenRecording[];
  screen_recording_folder?: string;
};

/** Codes only; the delivered text never comes back. */
export type DictationAnywhereDeliveryReport = {
  recording_id: string;
  /** Null when the history could not be written. */
  history_id: string | null;
  delivery: DictationAnywhereDelivery;
  delivery_code: string | null;
  ai: DictationAnywhereAiState;
  ai_code: string | null;
  /** Lengths in characters: the transcript and what went to the clipboard. */
  raw_chars: number;
  delivered_chars: number;
  /** Marked screenshots delivered with the text, and how many were pasted. */
  images: number;
  images_pasted: number;
  screen_recordings?: number;
};

export type DictationAnywhereRetryReport = {
  history_id: string;
  ai: DictationAnywhereAiState;
  ai_code: string | null;
  /** Whether the improved text is on the clipboard. */
  copied: boolean;
};

function requireDesktop(): void {
  if (!isTauri()) throw new Error("Dictation anywhere needs the desktop app");
}

/** A recording started: warm up the frontmost app's accessibility tree and,
 *  when a stop could rewrite the text, start the runner that pays for an
 *  early start. `prompts` are every prompt a stop could apply (the stop picks
 *  one); `connectedRuntimes` is what the delivery will send; no text goes. */
export async function prepareDictationAnywhere(
  recordingId: string,
  prompts: DictationAnywherePrompt[],
  connectedRuntimes: string[],
): Promise<void> {
  if (!isTauri()) return;
  await invoke("dictation_anywhere_prepare", {
    recordingId,
    prompts,
    connectedRuntimes,
  });
}

const imageStarts = new Map<string, Promise<number>>();

/** Numbers only; destination text and native control handles stay in Rust. */
export async function dictationAnywhereImageStart(
  recordingId: string,
): Promise<number> {
  return imageStarts.get(recordingId) ?? 1;
}

/** The user stopped talking: Rust records the recipient and image sequence. */
export async function markDictationAnywhereStop(
  recordingId: string,
): Promise<void> {
  if (!isTauri()) return;
  let pending = imageStarts.get(recordingId);
  if (!pending) {
    pending = invoke<number>("dictation_anywhere_mark_stop", { recordingId })
      .then((start) =>
        Number.isInteger(start) && start >= 1 && start <= 4_294_967_289
          ? start
          : 1,
      )
      .catch(() => 1);
    if (imageStarts.size >= 4)
      imageStarts.delete(imageStarts.keys().next().value!);
    imageStarts.set(recordingId, pending);
  }
  await pending;
}

/** Save, optionally improve, copy and paste a final transcript. */
export async function deliverDictationAnywhere(
  request: DictationAnywhereDeliverRequest,
): Promise<DictationAnywhereDeliveryReport> {
  requireDesktop();
  return invoke<DictationAnywhereDeliveryReport>("dictation_anywhere_deliver", {
    request,
  });
}

/** Admit attachment paste immediately; native work continues alongside speech. */
export async function startDictationAnywhereAttachments(
  request: Pick<
    DictationAnywhereDeliverRequest,
    | "recording_id"
    | "auto_paste"
    | "screen_recordings"
    | "screen_recording_folder"
  >,
): Promise<void> {
  requireDesktop();
  await invoke("dictation_anywhere_start_attachments", { request });
}

export async function discardDictationAnywhereAttachments(
  recordingId: string,
): Promise<void> {
  if (!isTauri()) return;
  await invoke("dictation_anywhere_discard_attachments", { recordingId });
}

/** Stages a PNG ahead of delivery. Recording previews are fallback-only;
 *  marked screenshots remain independent attachments, in `index` order. */
export async function stageDictationAnywhereImage(
  recordingId: string,
  index: number,
  png: Uint8Array,
  expiresAtMs?: number,
  kind?: "recording-preview",
): Promise<void> {
  requireDesktop();
  await invoke("dictation_anywhere_stage_image", png, {
    headers: {
      "x-recording-id": recordingId,
      "x-image-index": String(index),
      ...(kind ? { "x-image-kind": kind } : {}),
      ...(expiresAtMs === undefined
        ? {}
        : { "x-stage-expires-at-ms": String(expiresAtMs) }),
    },
  });
}

/** The keys the flow claims system-wide, so they never reach the app in
 *  front. While `active`, the cancel key comes to the flow as
 *  `dictation-anywhere-escape`; Command+1 to Command+`promptKeys` (Windows+N
 *  on Windows) come as `dictation-anywhere-prompt` with `{ prompt: n }`.
 *  `promptKeys` is non-zero only while the flow listens. */
export async function setDictationAnywhereClaims(
  active: boolean,
  promptKeys: number,
): Promise<void> {
  if (!isTauri()) return;
  await invoke("set_dictation_anywhere_active", { active, promptKeys });
}

/** Escape during the AI step: deliver the original now. */
export async function skipDictationAnywherePolish(
  recordingId: string,
): Promise<void> {
  if (!isTauri()) return;
  await invoke("dictation_anywhere_skip_polish", { recordingId });
}

/** Rerun the AI step on a saved dictation; the result goes to the clipboard. */
export async function retryDictationAnywhere(
  historyId: string,
  prompts: DictationAnywherePrompt[],
  connectedRuntimes: string[],
): Promise<DictationAnywhereRetryReport> {
  requireDesktop();
  return invoke<DictationAnywhereRetryReport>("dictation_anywhere_retry", {
    historyId,
    prompts,
    connectedRuntimes,
  });
}

/** Copy a saved dictation's original or improved text to the clipboard. */
export async function copyDictationAnywhere(
  historyId: string,
  which: "original" | "improved",
): Promise<void> {
  requireDesktop();
  await invoke("dictation_anywhere_copy", { historyId, which });
}

/** Copy one of a saved dictation's marked screenshots to the clipboard. */
export async function copyDictationAnywhereImage(
  historyId: string,
  index: number,
): Promise<void> {
  requireDesktop();
  await invoke("dictation_anywhere_copy_image", { historyId, index });
}

/** One of a saved dictation's marked screenshots, as PNG bytes. */
export async function readDictationAnywhereImage(
  historyId: string,
  index: number,
): Promise<ArrayBuffer> {
  requireDesktop();
  return invoke<ArrayBuffer>("dictation_anywhere_history_image", {
    historyId,
    index,
  });
}
